import React, { useCallback, useEffect, useState } from "react";
import { BellRing, RefreshCw, Send, Shield } from "lucide-react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Card, CardContent } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Spinner } from "../../components/ui/spinner";
import { Alert, AlertDescription } from "../../components/ui/alert";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../../components/ui/dialog";
import { getOnlineUsers, getUsers, apiSendNotification } from "../../lib/api";
import { useToast } from "../../contexts/ToastContext";
import { sameEmail } from "../../lib/utils";

const ROLE_VARIANT = {
  FleetAdmin: "info",
  ReleaseManager: "success",
  SupportEngineer: "warning",
  Nurse: "secondary",
  Patient: "muted",
};

// The send Lambda enforces these; mirroring them here means the user sees the
// error while typing instead of after a round trip per recipient.
const MAX_HEADER_CHARS = 200;
const MAX_CONTENT_CHARS = 1000;

// One page of the directory is enough to pick recipients from — this is a
// compose screen, not the user admin table.
const OFFLINE_FETCH_LIMIT = 100;

// The send Lambda rejects a batch larger than this, so select-all over a long
// directory has to be caught here rather than at the round trip.
const MAX_RECIPIENTS = 50;

const composeSchema = z.object({
  header: z.string().trim().min(1, "Header required").max(MAX_HEADER_CHARS, `At most ${MAX_HEADER_CHARS} characters`),
  content: z.string().trim().min(1, "Message required").max(MAX_CONTENT_CHARS, `At most ${MAX_CONTENT_CHARS} characters`),
});

function displayName(u) {
  return u.fullname || u.name || u.email;
}

export default function SendNotificationPage() {
  const { toast } = useToast();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [includeOffline, setIncludeOffline] = useState(false);
  // Emails, lowercased — the same key the online list and the directory agree on.
  const [selected, setSelected] = useState([]);
  const [composeOpen, setComposeOpen] = useState(false);
  const [sending, setSending] = useState(false);

  /**
   * Load the recipient list.
   *
   * The online list is the source of truth for who is reachable right now.
   * Merging in the directory is what makes offline users selectable at all —
   * sending to them is legitimate (the Lambda stores before it pushes), so the
   * only difference is the badge.
   */
  const loadUsers = useCallback(async (withOffline) => {
    setLoading(true);
    setLoadError("");
    try {
      const online = await getOnlineUsers();
      const rows = online.users.map((u) => ({
        email: (u.email || "").trim().toLowerCase(),
        fullname: u.fullname || "",
        role: u.role || "",
        connections: u.connections || 0,
        online: true,
      }));

      if (withOffline) {
        // Best-effort: losing the directory costs the offline rows, not the
        // online ones, so a failure here must not empty the table.
        try {
          const directory = await getUsers({ page: 1, limit: OFFLINE_FETCH_LIMIT });
          (directory.users || []).forEach((u) => {
            const email = (u.email || "").trim().toLowerCase();
            if (!email || rows.some((r) => sameEmail(r.email, email))) return;
            rows.push({
              email,
              fullname: u.fullname || u.name || "",
              role: u.current_role || u.role || "",
              connections: 0,
              online: false,
            });
          });
        } catch (err) {
          setLoadError(err?.message || "Could not load offline users.");
        }
      }

      // Online first, then alphabetically — the people who will see it now sit
      // at the top of the list.
      rows.sort((a, b) => {
        if (a.online !== b.online) return a.online ? -1 : 1;
        return displayName(a).localeCompare(displayName(b));
      });
      setUsers(rows);
    } catch (err) {
      setUsers([]);
      setLoadError(err?.message || "Could not load users.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadUsers(includeOffline);
  }, [loadUsers, includeOffline]);

  // Drop selections that are no longer in the list, so the count can never
  // exceed what is on screen after a refresh or an offline toggle.
  useEffect(() => {
    setSelected((prev) => prev.filter((email) => users.some((u) => sameEmail(u.email, email))));
  }, [users]);

  const { register, handleSubmit, reset, watch, formState: { errors } } = useForm({
    resolver: zodResolver(composeSchema),
    defaultValues: { header: "", content: "" },
  });
  const contentValue = watch("content") || "";

  const isSelected = (email) => selected.some((e) => sameEmail(e, email));
  const allSelected = users.length > 0 && selected.length === users.length;

  const toggleUser = (email) => {
    setSelected((prev) => {
      if (prev.some((e) => sameEmail(e, email))) {
        return prev.filter((e) => !sameEmail(e, email));
      }
      if (prev.length >= MAX_RECIPIENTS) {
        toast({
          title: `At most ${MAX_RECIPIENTS} recipients`,
          description: "Send this batch first, then select the rest.",
          variant: "warning",
        });
        return prev;
      }
      return [...prev, email];
    });
  };

  const toggleAll = () => {
    if (allSelected) {
      setSelected([]);
      return;
    }
    const all = users.map((u) => u.email);
    if (all.length > MAX_RECIPIENTS) {
      setSelected(all.slice(0, MAX_RECIPIENTS));
      toast({
        title: `Selected the first ${MAX_RECIPIENTS}`,
        description: `One notification can reach at most ${MAX_RECIPIENTS} users. Send in batches.`,
        variant: "warning",
      });
      return;
    }
    setSelected(all);
  };

  const openCompose = () => {
    reset({ header: "", content: "" });
    setComposeOpen(true);
  };

  /**
   * Send to every selected recipient.
   *
   * One request for the whole batch — the Lambda takes an `emails` list and
   * authorises the caller once. It reports per-recipient outcomes, so a partial
   * send (207) comes back as a normal response with the failures named, not as a
   * throw.
   */
  const onSend = async ({ header, content }) => {
    const recipients = users.filter((u) => isSelected(u.email));
    setSending(true);
    try {
      const result = await apiSendNotification({
        emails: recipients.map((u) => u.email),
        header,
        content,
      });

      const failed = result.failed || [];
      const sent = result.sent ?? recipients.length - failed.length;

      if (failed.length > 0) {
        // Keep the failures selected and the dialog open so the same message can
        // be retried against exactly the addresses that did not get it.
        setSelected(failed);
        toast({
          title: sent > 0 ? `Sent to ${sent}, failed for ${failed.length}` : "Send failed",
          description: `Not delivered to ${failed.join(", ")}. The message is still here — try again.`,
          variant: "destructive",
        });
        return;
      }

      // delivered counts open tabs, not people; a recipient with no tab open
      // still has the notification stored, which is not a failure.
      const reachedLive = (result.recipients || []).filter((r) => r.delivered > 0).length;
      const queued = sent - reachedLive;
      toast({
        title: sent === 1 ? "Notification sent" : `Notification sent to ${sent} users`,
        description: queued > 0
          ? `${reachedLive} delivered now, ${queued} waiting for the recipient to open the portal.`
          : "Delivered to every recipient's open portal.",
        variant: "success",
      });
      setSelected([]);
      setComposeOpen(false);
      reset({ header: "", content: "" });
    } catch (err) {
      toast({
        title: "Send failed",
        description: err?.message || "Unable to send the notification.",
        variant: "destructive",
      });
    } finally {
      setSending(false);
    }
  };

  const onlineCount = users.filter((u) => u.online).length;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-slate-900">Send Notification</h2>
          <p className="text-sm text-slate-500">
            {onlineCount} {onlineCount === 1 ? "user" : "users"} online
            {selected.length > 0 && ` · ${selected.length} selected`}
          </p>
        </div>
        <Button onClick={openCompose} disabled={selected.length === 0 || loading}>
          <BellRing className="mr-2 h-4 w-4" />
          {selected.length > 1 ? `Notify ${selected.length} Users` : "Notify User"}
        </Button>
      </div>

      {loadError && (
        <Alert variant="warning">
          <AlertDescription>{loadError}</AlertDescription>
        </Alert>
      )}

      <div className="flex items-center justify-between text-xs text-slate-400">
        <label htmlFor="include-offline" className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer select-none">
          <input
            id="include-offline"
            type="checkbox"
            className="w-4 h-4 rounded border-slate-300 accent-[#08148C]"
            checked={includeOffline}
            onChange={(e) => setIncludeOffline(e.target.checked)}
          />{" "}
          Include offline users
        </label>
        <button
          type="button"
          onClick={() => loadUsers(includeOffline)}
          disabled={loading}
          className="flex items-center gap-1.5 hover:text-slate-600 disabled:opacity-50"
        >
          <RefreshCw className="h-3.5 w-3.5" /> Refresh
        </button>
      </div>

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-10">
                  <input
                    type="checkbox"
                    aria-label="Select all users"
                    className="w-4 h-4 rounded border-slate-300 accent-[#08148C]"
                    checked={allSelected}
                    onChange={toggleAll}
                    disabled={loading || users.length === 0}
                  />
                </TableHead>
                <TableHead>User</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && (
                <TableRow>
                  <TableCell colSpan={4} className="text-center py-20">
                    <div className="flex items-center justify-center">
                      <Spinner size="lg" />
                    </div>
                  </TableCell>
                </TableRow>
              )}
              {!loading && users.length === 0 && (
                <TableRow>
                  <TableCell colSpan={4} className="text-center py-10 text-sm text-slate-500">
                    {includeOffline
                      ? "No users found."
                      : "No users are online right now — tick Include offline users to notify someone anyway."}
                  </TableCell>
                </TableRow>
              )}
              {!loading && users.map((u) => {
                const checked = isSelected(u.email);
                return (
                  <TableRow key={u.email} className={checked ? "bg-blue-50/50" : undefined}>
                    <TableCell>
                      <input
                        type="checkbox"
                        aria-label={`Select ${displayName(u)}`}
                        className="w-4 h-4 rounded border-slate-300 accent-[#08148C]"
                        checked={checked}
                        onChange={() => toggleUser(u.email)}
                      />
                    </TableCell>
                    <TableCell>
                      <div>
                        <p className="font-medium text-sm">{u.fullname || u.email}</p>
                        <p className="text-xs text-slate-400">{u.email}</p>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1.5">
                        <Shield className="h-3.5 w-3.5 text-slate-400" />
                        <Badge variant={ROLE_VARIANT[u.role] || "muted"}>{u.role || "—"}</Badge>
                      </div>
                    </TableCell>
                    <TableCell>
                      {u.online ? (
                        <Badge variant="success">
                          Online{u.connections > 1 ? ` · ${u.connections} tabs` : ""}
                        </Badge>
                      ) : (
                        <Badge variant="muted">Offline</Badge>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Compose dialog */}
      <Dialog open={composeOpen} onOpenChange={(open) => { if (!sending) setComposeOpen(open); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {selected.length === 1
                ? `Send to ${displayName(users.find((u) => isSelected(u.email)) || { email: selected[0] })}`
                : `Send to ${selected.length} users`}
            </DialogTitle>
            <DialogDescription>
              Recipients with the portal open see it immediately; anyone else gets it the
              next time they sign in.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit(onSend)} className="space-y-3 py-2">
            {selected.length > 1 && (
              <div className="flex flex-wrap gap-1.5">
                {users.filter((u) => isSelected(u.email)).map((u) => (
                  <span
                    key={u.email}
                    className="inline-flex items-center gap-1 text-xs bg-blue-50 border border-blue-200 text-blue-700 rounded-full px-2.5 py-0.5 font-medium"
                  >
                    {displayName(u)}
                  </span>
                ))}
              </div>
            )}

            <div className="space-y-1.5">
              <Label htmlFor="notif-header">Header</Label>
              <Input id="notif-header" placeholder="Deploy complete" {...register("header")} />
              {errors.header && <p className="text-xs text-red-600">{errors.header.message}</p>}
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="notif-content">Message</Label>
              <Textarea
                id="notif-content"
                rows={4}
                placeholder="Baseline v2.1 has rolled out to All Clinicians."
                {...register("content")}
              />
              <div className="flex items-center justify-between">
                {errors.content
                  ? <p className="text-xs text-red-600">{errors.content.message}</p>
                  : <span />}
                <p className="text-xs text-slate-400">{contentValue.length}/{MAX_CONTENT_CHARS}</p>
              </div>
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setComposeOpen(false)}
                disabled={sending}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={sending || selected.length === 0}>
                {sending
                  ? <><Spinner size="sm" className="mr-2" /> Sending…</>
                  : <><Send className="mr-2 h-4 w-4" /> Send</>}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
